import React, { useMemo, useState, useEffect } from 'react';
import {
  Menu, X, Trophy, Beer, Users, Target, Phone,
  UtensilsCrossed, Send, Newspaper, Anchor, Crown,
  Calendar, Loader2, ChevronDown,
} from 'lucide-react';

import useContent from '../hooks/useContent';
import { getStandings } from '../lib/standings';
import { formatDate, byDateDesc, teamLabel } from '../lib/format';

const ICONS = { Beer, Target, Newspaper, Trophy, Users, Anchor, Crown, UtensilsCrossed };

const theme = {
  bg: 'bg-[#F9F9F9]',
  headerBlue: 'text-[#009FE3]',
  text: 'text-black',
};

const ITEMS_PER_LOAD = 3;

export default function HomePage() {
  const { content, loading } = useContent();
  const { karte, boce, reports, players, gallery, settings } = content;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('karte');
  const [visibleCardCount, setVisibleCardCount] = useState(ITEMS_PER_LOAD);
  const [visibleBocceCount, setVisibleBocceCount] = useState(ITEMS_PER_LOAD);

  const [formData, setFormData] = useState({
    sender: '', phone: '', category: '', message: '',
  });
  const [formStatus, setFormStatus] = useState('idle');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sortedCardResults = useMemo(() => [...karte].sort(byDateDesc), [karte]);
  const sortedBocceResults = useMemo(() => [...boce].sort(byDateDesc), [boce]);
  const sortedReports = useMemo(() => [...reports].sort(byDateDesc), [reports]);
  const cardStandings = useMemo(() => getStandings(karte, 'karte'), [karte]);
  const bocceStandings = useMemo(() => getStandings(boce, 'boce'), [boce]);

  const categories = settings?.contact?.categories?.length
    ? settings.contact.categories
    : ['Ostalo'];

  const toggleMenu = () => setIsMenuOpen((v) => !v);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = settings?.contact?.webhookUrl;
    if (!url) { setFormStatus('error'); return; }
    setFormStatus('submitting');
    try {
      const payload = { ...formData, category: formData.category || categories[0] };
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      setFormStatus('success');
      setFormData({ sender: '', phone: '', category: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 5000);
    } catch (error) {
      console.error('Greška pri slanju:', error);
      setFormStatus('error');
    }
  };

  const handleLoadMore = (type) => {
    if (type === 'karte') setVisibleCardCount((p) => p + ITEMS_PER_LOAD);
    else setVisibleBocceCount((p) => p + ITEMS_PER_LOAD);
  };

  // zajednički renderer za karticu meča (karte + boće)
  const renderMatchCard = ({ date, teamA, teamB, rows }) => (
    <div className="relative animate-in fade-in duration-700">
      <div className="absolute -top-4 -left-2 sm:-left-4 bg-black text-white px-2 py-1 text-[10px] sm:text-xs font-bold uppercase tracking-widest transform -rotate-2 z-10 flex items-center gap-2">
        <Calendar size={12} /> {formatDate(date)}
      </div>

      <div className="text-center border-b-2 border-black pb-4 mb-6 pt-4">
        <h3 className="text-lg sm:text-2xl font-black uppercase flex flex-col sm:flex-row items-center justify-center gap-2">
          <span>{teamA}</span>
          <span className="text-[#009FE3] px-2 text-sm font-serif italic normal-case">protiv</span>
          <span>{teamB}</span>
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full font-serif text-sm sm:text-lg">
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-b border-gray-300 last:border-0 hover:bg-gray-100 transition-colors">
                <td className="py-3 uppercase font-bold text-gray-600 w-1/2 align-top">{row.label}</td>
                <td className="py-3 text-right font-black text-xl w-1/2">
                  {row.value}
                  {row.sub && <div className="text-[10px] font-normal text-gray-500 mt-1">{row.sub}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderStandings = (rows, label) => (
    <div>
      <h4 className="text-sm font-bold uppercase mb-2 border-b border-gray-300 pb-1 flex items-center gap-2">
        <Trophy size={14} /> Ljestvica — {label}
      </h4>
      <table className="w-full text-xs sm:text-sm font-serif">
        <thead>
          <tr className="bg-black text-white">
            <th className="py-2 px-2 text-left">#</th>
            <th className="py-2 px-2 text-left">Igrač</th>
            <th className="py-2 px-2 text-right">Postotak</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={3} className="py-3 text-center text-gray-500 italic">Nema još odigranih mečeva.</td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr key={row.name} className="border-b border-gray-200 hover:bg-gray-50">
                <td className="py-2 px-2 font-bold text-gray-500">{idx + 1}.</td>
                <td className="py-2 px-2 font-semibold uppercase">
                  {row.name}
                  <span className="block text-[9px] text-gray-500 font-normal normal-case">
                    {row.wins} / {row.played} mečeva
                  </span>
                </td>
                <td className="py-2 px-2 text-right font-black">{row.percent.toFixed(1)}%</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bg} flex flex-col items-center justify-center font-serif`}>
        <Loader2 size={40} className="animate-spin text-[#009FE3] mb-4" />
        <p className="uppercase tracking-widest text-xs font-bold text-gray-500">Vrti se rotacija…</p>
      </div>
    );
  }

  const navItems = ['Izvještaji', 'Ekipa', 'Rezultati', 'Kontakt'];

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} font-serif selection:bg-[#009FE3] selection:text-white overflow-x-hidden`}>

      {/* HEADER */}
      <nav className={`fixed w-full z-50 transition-all duration-300 border-b-4 border-black ${scrolled ? 'bg-white py-2 shadow-lg' : 'bg-white py-2 md:py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-[10px] md:text-xs font-sans font-bold border-b border-gray-300 pb-1 mb-1 uppercase tracking-wider text-gray-500">
            <span>{settings?.masthead?.city}</span>
            <span className="hidden sm:inline">{settings?.masthead?.issue}</span>
            <span>{settings?.masthead?.price}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
              <a href="/" className="group">
                <h1 className={`text-4xl sm:text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none font-serif ${theme.headerBlue} drop-shadow-sm group-hover:opacity-90 transition-opacity`}>
                  KRDO<span className="text-black text-xs md:text-lg align-top px-1">•</span>DEVA
                </h1>
                <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.4em] text-black bg-gray-100 px-1 mt-[-5px] md:mt-[-10px] text-center">
                  {settings?.masthead?.tagline}
                </div>
              </a>
            </div>

            <div className="hidden lg:flex items-center">
              <div className="flex space-x-6 font-bold text-sm font-sans uppercase tracking-tight items-center text-gray-800">
                {navItems.map((item) => (
                  <a key={item} href={`#${item.toLowerCase()}`} className="hover:text-[#009FE3] hover:underline decoration-2 underline-offset-4 transition-all">
                    {item}
                  </a>
                ))}
              </div>
              <a href="#kontakt" className="ml-6 bg-[#009FE3] text-white px-5 py-2 font-bold uppercase hover:bg-black transition-colors shadow-md text-sm">
                Doniraj
              </a>
            </div>

            <div className="flex lg:hidden">
              <button onClick={toggleMenu} className="p-2 text-black hover:text-[#009FE3]" aria-label="Izbornik">
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-b-4 border-black absolute w-full z-50 shadow-xl animate-in slide-in-from-top-5">
            <div className="flex flex-col p-4 space-y-4 font-sans font-bold uppercase text-lg text-center">
              {navItems.map((item) => (
                <a key={item} href={`#${item.toLowerCase()}`} onClick={() => setIsMenuOpen(false)} className="py-3 border-b border-gray-100 hover:text-[#009FE3]">
                  {item}
                </a>
              ))}
              <a href="#kontakt" onClick={() => setIsMenuOpen(false)} className="bg-[#009FE3] text-white py-3 mt-2 rounded-sm shadow">
                Doniraj Janje
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <header className="relative pt-28 md:pt-40 pb-12 px-4 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 border-b-2 border-black pb-8">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-4 border-t-2 border-black pt-2">
              <span className="bg-[#009FE3] text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                {settings?.hero?.kicker}
              </span>
              <span className="text-xs font-bold uppercase text-gray-500">{settings?.hero?.locations}</span>
            </div>

            <h2 className="text-5xl sm:text-6xl md:text-8xl font-black font-serif mb-6 leading-[0.9] tracking-tight text-black">
              {settings?.hero?.headlineTop}<br />
              <span className={`italic ${theme.headerBlue}`}>{settings?.hero?.headlineBottom}</span>
            </h2>

            <div className="md:columns-2 gap-8 text-lg font-serif leading-relaxed text-justify mb-8 text-gray-900 border-t border-gray-300 pt-4">
              <p className="mb-4 md:mb-0">
                <span className="text-5xl float-left mr-2 mt-[-10px] font-black text-[#009FE3]">
                  {(settings?.hero?.lead1 || 'R').charAt(0)}
                </span>
                {(settings?.hero?.lead1 || '').slice(1)}
              </p>
              <p>{settings?.hero?.lead2}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <a href="#izvještaji" className="bg-black text-white px-8 py-3 font-bold uppercase hover:bg-[#009FE3] transition-colors flex items-center justify-center gap-2 text-sm sm:text-base">
                <Newspaper size={18} /> Službeni Izvještaj
              </a>
              <a href="#rezultati" className="border-2 border-black text-black px-8 py-3 font-bold uppercase hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base">
                <Trophy size={18} /> Semafor
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 lg:border-l-2 lg:border-black lg:pl-8 flex flex-col gap-6">
            <div className="border border-black p-4 bg-white text-center shadow-[4px_4px_0px_0px_rgba(0,159,227,0.2)]">
              <div className="border-b border-black pb-2 mb-2 flex justify-center text-[#009FE3]">
                <Anchor className="h-10 w-10" />
              </div>
              <h3 className="font-bold uppercase text-sm mb-1">{settings?.sidebar?.seaTitle}</h3>
              <p className="font-serif italic text-sm">{settings?.sidebar?.seaText}</p>
            </div>

            <div className="border-2 border-dashed border-gray-400 p-6 text-center bg-gray-50">
              <h3 className="font-black text-xl mb-2 text-[#009FE3]">{settings?.sidebar?.wantedTitle}</h3>
              <p className="text-sm font-serif mb-4 leading-tight">
                {settings?.sidebar?.wantedText}
                <br /><span className="font-bold">{settings?.sidebar?.wantedCode}</span>
              </p>
              <Crown className="mx-auto text-black h-6 w-6 opacity-50" />
            </div>

            <div className="bg-[#009FE3] text-white p-4 text-center">
              <p className="font-bold uppercase text-xs mb-2 border-b border-white/30 pb-1">Citat Dana</p>
              <p className="font-serif italic font-medium">"{settings?.sidebar?.quote}"</p>
              <p className="text-xs mt-2 opacity-80">{settings?.sidebar?.quoteAuthor}</p>
            </div>
          </div>
        </div>
      </header>

      {/* PHILOSOPHY */}
      <section id="filozofija" className="py-16 px-4 bg-[#F9F9F9] border-b-2 border-black">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-12">
            <div className="h-[2px] w-12 bg-black"></div>
            <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tight text-center">Riječ Uredništva</h2>
            <div className="h-[2px] w-12 bg-black"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-0 border-t-2 border-b-2 border-black">
            {(settings?.philosophy || []).map((item, i, arr) => {
              const Icon = ICONS[item.icon] || Newspaper;
              return (
                <div key={i} className={`p-8 ${i !== arr.length - 1 ? 'md:border-r border-black border-dashed' : ''} text-center hover:bg-gray-50 transition-colors`}>
                  <Icon className="h-10 w-10 mx-auto mb-4 text-[#009FE3]" />
                  <h3 className="text-lg font-black uppercase mb-2">{item.title}</h3>
                  <p className="font-serif text-gray-700 leading-snug">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROSTER */}
      <section id="ekipa" className="py-16 px-4 bg-white border-b-2 border-black">
        <div className="max-w-7xl mx-auto">
          <div className="border-b-4 border-black mb-10 pb-2">
            <h2 className="text-3xl sm:text-5xl font-black uppercase text-[#009FE3]">
              Igrački Kadar
              <span className="text-black text-lg align-middle ml-2 font-serif italic normal-case">
                {settings?.roster?.season}
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {players.map((member) => {
              const cardStats = member.statKey ? cardStandings.find((s) => s.name === member.statKey) : null;
              const bocceStats = member.statKey ? bocceStandings.find((s) => s.name === member.statKey) : null;

              return (
                <div key={member.id} className="bg-white border border-gray-300 p-3 shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col h-full group">
                  <div className="relative aspect-[4/5] border border-black mb-3 overflow-hidden bg-gray-200">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover transition-all duration-700 ease-in-out hover:sepia hover:scale-110 hover:contrast-125"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="absolute inset-0 hidden flex-col items-center justify-center bg-gray-100 text-gray-400 p-4 text-center">
                      <Users size={48} className="mb-2 opacity-50" />
                      <span className="text-[10px] font-bold uppercase tracking-widest">Nema Slike</span>
                    </div>
                  </div>

                  <div className="flex-grow flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-black uppercase leading-tight mb-2 font-sans group-hover:text-[#009FE3] transition-colors">
                        {member.name}
                      </h3>
                      <div className="mb-2">
                        <span className="inline-block bg-black text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                          {member.role}
                        </span>
                      </div>

                      {(cardStats || bocceStats) && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {cardStats && (
                            <span className="inline-flex items-center gap-1 bg-[#009FE3]/10 text-[#009FE3] border border-[#009FE3] text-[10px] font-bold uppercase px-2 py-0.5 tracking-wider">
                              <Trophy size={10} />
                              Karte {cardStats.percent.toFixed(1)}%
                              <span className="text-[9px] text-gray-600 normal-case font-normal">
                                ({cardStats.wins}/{cardStats.played})
                              </span>
                            </span>
                          )}
                          {bocceStats && (
                            <span className="inline-flex items-center gap-1 bg-black/5 text-black border border-black text-[10px] font-bold uppercase px-2 py-0.5 tracking-wider">
                              <Trophy size={10} />
                              Boće {bocceStats.percent.toFixed(1)}%
                              <span className="text-[9px] text-gray-600 normal-case font-normal">
                                ({bocceStats.wins}/{bocceStats.played})
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-serif leading-snug text-gray-800 border-t border-gray-200 pt-2 italic">
                      "{member.desc}"
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REZULTATI + LJESTVICE */}
      <section id="rezultati" className="py-16 px-4 bg-[#F9F9F9]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-4">
            <span className="bg-[#009FE3] text-white px-3 py-1 text-xs font-bold uppercase tracking-widest mb-2 inline-block">Sportski Dodatak</span>
            <h2 className="text-4xl sm:text-5xl font-black uppercase">Semafor</h2>
          </div>

          <div className="mt-8 mb-10 border-4 border-black bg-white p-4 sm:p-6">
            <div className="text-center mb-4">
              <h3 className="text-xl sm:text-2xl font-black uppercase">Ljestvice</h3>
              <p className="text-xs sm:text-sm text-gray-600 font-serif mt-1">
                Ljestvica prikazuje postotak pobjeda po igraču (dobiveni mečevi / odigrani mečevi).
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {renderStandings(cardStandings, 'Karte')}
              {renderStandings(bocceStandings, 'Boće')}
            </div>
          </div>

          <div className="flex justify-center gap-2 sm:gap-4 mb-8">
            {[['karte', 'Karte'], ['boce', 'Boće']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 sm:flex-none px-4 sm:px-8 py-3 border-2 border-black font-bold uppercase text-sm sm:text-base transition-all ${activeTab === key ? 'bg-[#009FE3] text-white border-[#009FE3]' : 'bg-white text-black hover:bg-gray-100'}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="border-4 border-black p-1 bg-white">
            <div className="border border-black bg-gray-50 p-4 sm:p-8">
              {activeTab === 'karte' ? (
                <div className="space-y-12">
                  {sortedCardResults.slice(0, visibleCardCount).map((match) => (
                    <React.Fragment key={match.id}>
                      {renderMatchCard({
                        date: match.date,
                        teamA: teamLabel(match.teamA),
                        teamB: teamLabel(match.teamB),
                        rows: (match.games || []).map((g) => ({ label: g.name, value: `${g.a} – ${g.b}` })),
                      })}
                    </React.Fragment>
                  ))}

                  {visibleCardCount < sortedCardResults.length && (
                    <div className="text-center pt-8 border-t-2 border-dashed border-gray-300">
                      <button
                        onClick={() => handleLoadMore('karte')}
                        className="bg-white border-2 border-black px-6 py-2 text-sm font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)]"
                      >
                        Vidi Starije Rezultate (+3)
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-12">
                  {sortedBocceResults.slice(0, visibleBocceCount).map((match) => {
                    const rows = [
                      { label: 'Rezultat', value: `${match.scoreA} – ${match.scoreB}`, sub: match.status },
                      match.note ? { label: 'Bilješka', value: match.note } : null,
                    ].filter(Boolean);

                    return (
                      <React.Fragment key={match.id}>
                        {renderMatchCard({
                          date: match.date,
                          teamA: teamLabel(match.teamA, ', '),
                          teamB: teamLabel(match.teamB, ', '),
                          rows,
                        })}
                      </React.Fragment>
                    );
                  })}

                  {visibleBocceCount < sortedBocceResults.length && (
                    <div className="text-center pt-8 border-t-2 border-dashed border-gray-300">
                      <button
                        onClick={() => handleLoadMore('boce')}
                        className="bg-white border-2 border-black px-6 py-2 text-sm font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)]"
                      >
                        Učitaj Još Susreta
                      </button>
                    </div>
                  )}
                </div>
              )}

              {((activeTab === 'karte' && visibleCardCount >= sortedCardResults.length)
                || (activeTab === 'boce' && visibleBocceCount >= sortedBocceResults.length)) ? (
                  <div className="text-center mt-6 text-xs uppercase tracking-widest text-gray-400">
                    Nema više rezultata u arhivi.
                  </div>
                ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* SLUŽBENI ZAPISNIK */}
      <section id="izvještaji" className="py-16 px-4 bg-white border-b-2 border-black">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="h-[2px] w-12 bg-black"></div>
            <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tight text-center">Službeni Zapisnik</h2>
            <div className="h-[2px] w-12 bg-black"></div>
          </div>

          {sortedReports.map((report) => (
            <article key={report.id} className="mb-12 last:mb-0">
              <div className="flex flex-col md:flex-row gap-6 md:gap-12 items-start">
                <div className="md:w-32 flex-shrink-0">
                  <div className="bg-black text-white px-3 py-2 text-center font-bold uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(0,159,227,1)]">
                    <span className="block text-xs opacity-75">Datum</span>
                    <span className="block text-sm">{formatDate(report.date)}</span>
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="text-2xl md:text-4xl font-black uppercase mb-4 leading-tight">{report.title}</h3>
                  <p className="text-lg md:text-xl font-serif font-bold text-gray-700 italic border-l-4 border-[#009FE3] pl-4 mb-6">
                    {report.intro}
                  </p>
                  <div className="prose prose-lg font-serif text-gray-900 text-justify">
                    {(report.paragraphs || []).map((para, idx) => (
                      <p key={idx} className="mb-4">{para}</p>
                    ))}
                  </div>

                  <div className="mt-6 pt-4 border-t border-dashed border-gray-300">
                    <a href="#rezultati" className="inline-flex items-center gap-2 text-[#009FE3] font-bold uppercase text-xs tracking-widest hover:text-black transition-colors">
                      Pogledaj točne rezultate na semaforu <ChevronDown size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* GALERIJA */}
      <section id="galerija" className="py-16 px-4 bg-[#F9F9F9] border-b-2 border-black">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="h-[2px] w-12 bg-black"></div>
            <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tight text-center">Galerija</h2>
            <div className="h-[2px] w-12 bg-black"></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {gallery.map((item, idx) => (
              <div key={item.id} className="relative aspect-[4/3] border border-black bg-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                <img
                  src={item.src}
                  alt={item.caption || `Krdo Deva ${idx + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 ease-out hover:scale-110"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="absolute inset-0 hidden items-center justify-center bg-gray-100 text-gray-500 text-[10px] uppercase tracking-widest font-bold">
                  Nema slike
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-center text-xs text-gray-500 font-serif italic">
            {settings?.gallery?.footnote}
          </p>
        </div>
      </section>

      {/* CONTACT */}
      <section id="kontakt" className="py-16 px-4 bg-white border-t border-black">
        <div className="max-w-2xl mx-auto border-2 border-black p-6 sm:p-10 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] relative">

          {formStatus === 'success' && (
            <div className="absolute inset-0 bg-white/95 z-20 flex flex-col items-center justify-center text-center p-8 animate-in fade-in">
              <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4 border-2 border-green-600">
                <Send size={32} />
              </div>
              <h3 className="text-2xl font-black uppercase mb-2">Poslano!</h3>
              <p className="font-serif text-lg">Vaše pismo je zaprimljeno. Uredništvo će ga razmotriti uz čašu bevande.</p>
            </div>
          )}

          <div className="text-center mb-8">
            <h2 className="text-3xl font-black uppercase mb-2">{settings?.contact?.title}</h2>
            <p className="font-serif italic text-gray-600">{settings?.contact?.subtitle}</p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="font-bold uppercase text-[10px] mb-1 tracking-wider">Pošiljatelj</label>
                <input
                  type="text" name="sender" value={formData.sender} onChange={handleInputChange} required
                  className="border border-black p-2 bg-gray-50 focus:outline-none focus:bg-white font-serif rounded-none disabled:opacity-50"
                  placeholder="Vaše Ime" disabled={formStatus === 'submitting'}
                />
              </div>
              <div className="flex flex-col">
                <label className="font-bold uppercase text-[10px] mb-1 tracking-wider">Broj Mobitela</label>
                <input
                  type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required
                  className="border border-black p-2 bg-gray-50 focus:outline-none focus:bg-white font-serif rounded-none disabled:opacity-50"
                  placeholder="09X 123 4567" disabled={formStatus === 'submitting'}
                />
              </div>
            </div>

            <div className="flex flex-col">
              <label className="font-bold uppercase text-[10px] mb-1 tracking-wider">Kategorija</label>
              <select
                name="category" value={formData.category || categories[0]} onChange={handleInputChange}
                className="border border-black p-2 bg-gray-50 focus:outline-none focus:bg-white font-serif rounded-none disabled:opacity-50"
                disabled={formStatus === 'submitting'}
              >
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="font-bold uppercase text-[10px] mb-1 tracking-wider">Sadržaj Pisma</label>
              <textarea
                name="message" value={formData.message} onChange={handleInputChange} required rows="3"
                className="border border-black p-2 bg-gray-50 focus:outline-none focus:bg-white font-serif rounded-none disabled:opacity-50"
                placeholder="Pišite nam..." disabled={formStatus === 'submitting'}
              ></textarea>
            </div>

            <button
              type="submit" disabled={formStatus === 'submitting'}
              className={`w-full py-3 font-bold uppercase tracking-widest flex justify-center items-center gap-2 rounded-none shadow-sm text-sm transition-all ${formStatus === 'submitting' ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-[#009FE3] text-white hover:bg-black'}`}
            >
              {formStatus === 'submitting'
                ? <><Loader2 size={16} className="animate-spin" /> Šaljem...</>
                : <><Send size={16} /> Pošalji Uredništvu</>}
            </button>

            {formStatus === 'error' && (
              <p className="text-red-600 text-xs font-bold text-center mt-2 uppercase">
                Greška pri slanju. Pokušajte ponovno (možda je nestalo interneta u konobi).
              </p>
            )}
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black text-white py-12 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-8">
          <div>
            <h3 className="text-2xl font-black uppercase mb-2 font-serif tracking-tight">KRDO DEVA</h3>
            <div className="text-[10px] uppercase tracking-widest text-gray-400 space-y-1">
              <p>{settings?.footer?.publisher}</p>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center border-y border-gray-800 py-4 md:border-0 md:py-0 w-full md:w-auto">
            <UtensilsCrossed size={24} className="mb-2 text-[#009FE3]" />
            <p className="font-bold uppercase text-xs">Redakcija:</p>
            <p className="font-serif italic text-sm">{settings?.footer?.redakcija}</p>
          </div>

          <div className="flex flex-col md:items-end justify-center">
            <div className="flex items-center gap-2 mb-2 justify-center md:justify-end">
              <Phone size={14} className="text-[#009FE3]" />
              <span className="text-xs font-bold">{settings?.footer?.phone}</span>
            </div>
            <p className="font-mono text-[10px] text-gray-500">{settings?.footer?.copyright}</p>
            <a href="/admin" className="font-mono text-[10px] text-gray-700 hover:text-gray-400 mt-1">uredništvo</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
